import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ConversionBlock, PriceTag } from '@/pages/guias/components/GuiaComponents';
import { ServiceBreadcrumb, SchemaService } from '../components/ServicioComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';
import { SchemaPriceOffer } from '@/components/feature/SchemaOrg';

export default function MantenimientoToldosMalaga() {
  return (
    <>
      <title>Mantenimiento de toldos en Málaga | Revisión, limpieza e impermeabilización</title>
      <meta
        name="description"
        content="Servicio de mantenimiento de toldos en Málaga. Revisión anual, limpieza de lonas, impermeabilización, ajuste de tensores y engrase de mecanismos. Evita averías antes del verano."
      />
      <meta
        name="keywords"
        content="mantenimiento toldos malaga, limpieza toldos malaga, impermeabilizar toldo malaga, revision toldos malaga, cuidar toldo malaga"
      />
      <link rel="canonical" href="https://toldos-malaga.es/mantenimiento-toldos-malaga" />
      <SchemaService
        name="Mantenimiento de toldos en Málaga"
        description="Servicio profesional de mantenimiento preventivo de toldos en Málaga. Incluye revisión completa de mecanismos, limpieza de lonas, impermeabilización, ajuste de tensores y engrase. Recomendado antes de la temporada de verano para evitar averías."
        priceRange="desde 60 €"
      />
      <SchemaPriceOffer price={110} name="Mantenimiento completo de toldo en Málaga" description="Revisión completa, limpieza de lona, impermeabilización y engrase de mecanismos." />
      <meta property="og:title" content="Mantenimiento de toldos en Málaga | Revisión, limpieza e impermeabilización | Toldos Málaga" />
      <meta property="og:description" content="Servicio de mantenimiento de toldos en Málaga. Revisión anual, limpieza de lonas, impermeabilización, ajuste de tensores y engrase de mecanismos. Evita averías antes del verano." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/mantenimiento-toldos-malaga" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Mantenimiento de toldos en Málaga | Toldos Málaga" />
      <meta name="twitter:description" content="Revisión anual, limpieza e impermeabilización de toldos. Evita averías antes del verano en Málaga." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />
      <Navbar />
      <main>
        <ServiceBreadcrumb currentTitle="Mantenimiento de toldos" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/025-servicio-mantenimiento-hero.webp"
              alt="Técnico realizando mantenimiento profesional de un toldo en Málaga"
              title="Mantenimiento de toldos en Málaga - Revisión profesional"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Servicio de mantenimiento
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Mantenimiento de toldos en Málaga
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              La mayoría de la gente solo se acuerda del toldo cuando falla. Y entonces la reparación cuesta el doble de lo que habría costado un simple mantenimiento preventivo. En Málaga, con sol intenso, viento de levante, salitre y calima, un toldo sin mantenimiento es un toldo con fecha de caducidad. Nosotros te ayudamos a alargar su vida útil y a que llegue al verano en perfectas condiciones.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Por qué el mantenimiento es especialmente importante en Málaga?
            </h2>

            <p>
              El clima malagueño es maravilloso para vivir, pero implacable con los toldos. Aquí tienes los cuatro enemigos de tu toldo y por qué el mantenimiento los mantiene a raya:
            </p>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-sun-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Sol extremo (+3.000 h/año).</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Los rayos UV degradan los pigmentos de la lona y resecan las juntas de goma del mecanismo. Una limpieza e impermeabilización anual con productos específicos crea una capa protectora que frena este deterioro.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-windy-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Viento de levante.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Las rachas fuertes aflojan los tornillos de fijación y desajustan los tensores. Durante la revisión comprobamos y reapretamos todas las fijaciones para que el próximo levante no te juegue una mala pasada.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-drop-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Salitre marino.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    La brisa del mar deposita partículas de sal en cada rincón metálico del toldo. Con el tiempo, corroe muelles, engranajes y tornillería. La limpieza profesional elimina estos depósitos y aplicamos lubricante protector en todas las piezas móviles.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mist-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Calima y polvo sahariano.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    La calima deposita una fina capa de polvo que se incrusta en el tejido y en los engranajes. Si no se limpia, esa capa se convierte en una pasta abrasiva que desgasta prematuramente las piezas móviles y deja la lona con un aspecto sucio permanente.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Qué incluye nuestro servicio de mantenimiento?
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-line text-primary-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Limpieza de lona</span>
                </div>
                <p className="text-xs text-foreground-600">
                  Cepillado en seco + limpieza con productos específicos que no dañan el tejido. Eliminamos polvo, manchas de humedad y restos de calima.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-line text-primary-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Impermeabilización</span>
                </div>
                <p className="text-xs text-foreground-600">
                  Aplicación de spray hidrófugo que protege la lona del agua de lluvia y evita la formación de moho en los meses húmedos.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-line text-primary-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Revisión de mecanismos</span>
                </div>
                <p className="text-xs text-foreground-600">
                  Comprobación de muelles, engranajes, ejes y rodamientos. Detectamos desgastes antes de que se conviertan en avería.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-line text-primary-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Engrase y lubricación</span>
                </div>
                <p className="text-xs text-foreground-600">
                  Aplicación de grasa específica en todas las piezas móviles. Reduce la fricción, el ruido y el desgaste prematuro.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-line text-primary-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Ajuste de tensores</span>
                </div>
                <p className="text-xs text-foreground-600">
                  Verificación y ajuste de la tensión de la lona en ambos brazos. Evitamos descolgamientos y flameos con el viento.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-line text-primary-500 text-lg"></i>
                  <span className="font-semibold text-foreground-900 text-sm">Revisión eléctrica</span>
                </div>
                <p className="text-xs text-foreground-600">
                  En toldos motorizados: comprobación de cableado, conectores, finales de carrera y funcionamiento del mando.
                </p>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Cada cuánto hay que hacer el mantenimiento?
            </h2>

            <p>
              En Málaga, recomendamos una revisión de mantenimiento una vez al año, idealmente entre marzo y abril, justo antes de que empiece la temporada fuerte de uso. Así tu toldo llega al verano en condiciones óptimas y reduces drásticamente el riesgo de averías en pleno agosto, que es cuando más se necesita y cuando más caras suelen ser las reparaciones urgentes.
            </p>

            <p>
              Para negocios de hostelería —bares, restaurantes, cafeterías con terraza—, donde el toldo se abre y se cierra varias veces al día y está sometido a un uso mucho más intensivo, recomendamos dos revisiones al año: una en marzo (pre-temporada) y otra en septiembre (post-verano), para evaluar los daños del verano y preparar el toldo para el otoño.
            </p>

            <div className="my-8">
              <ConversionBlock
                title="¿Tu toldo necesita una revisión antes del verano?"
                subtitle="Limpiamos, impermeabilizamos y revisamos tu toldo a fondo. Alarga su vida útil y evita averías en pleno agosto. Pide cita sin compromiso."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-8">
              <PriceTag price="desde 60 €" description="Mantenimiento básico" />
              <PriceTag price="desde 110 €" description="Mantenimiento completo + impermeabilización" />
              <p className="text-sm text-foreground-500">
                Precios orientativos para un toldo estándar de terraza. Incluye desplazamiento en Málaga capital.
              </p>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}